import { createElement, Fragment, useMemo } from 'react'

import { GlossarySubSection as GlossarySubSectionType } from '../../GlossarySidebar.types'
import * as S from './GlossarySubSection.styles'

interface GlossarySubSectionProps {
  subsection: GlossarySubSectionType
  searchTerm?: string
}

const createHighlightedText = (text: string, searchTerm: string) => {
  if (!searchTerm || !text) return text

  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')
  const parts = text.split(regex)

  return createElement(
    Fragment,
    null,
    ...parts.map((part, index) => {
      const isHighlighted = part ? regex.test(part) : false
      return createElement(
        S.HighlightedText,
        {
          key: index,
          isHighlighted,
        },
        part,
      )
    }),
  )
}

export function GlossarySubSection({ subsection, searchTerm }: GlossarySubSectionProps) {
  const highlightedTitle = useMemo(() => {
    return searchTerm ? createHighlightedText(subsection.title, searchTerm) : subsection.title
  }, [subsection.title, searchTerm])

  const highlightedDescription = useMemo(() => {
    return searchTerm && subsection.description
      ? createHighlightedText(subsection.description, searchTerm)
      : subsection.description
  }, [subsection.description, searchTerm])

  return (
    <S.SubSectionContainer data-section-id={subsection.id}>
      <S.SubSectionHeader>
        {subsection.badge ?? null}
        <S.SubSectionTitle>{highlightedTitle}</S.SubSectionTitle>
      </S.SubSectionHeader>

      {subsection.description && (
        <S.SubSectionDescription>{highlightedDescription}</S.SubSectionDescription>
      )}

      {subsection.customContent && (
        <S.CustomContentWrapper>{subsection.customContent}</S.CustomContentWrapper>
      )}
    </S.SubSectionContainer>
  )
}
